import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  TextField,
} from '@mui/material';
import { HighlightOffOutlined } from '@mui/icons-material';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import { PortalRole, useListCustomersQuery, UserProfile } from 'src/backend';
import { Customer } from 'src/pages/Dashboard/CreateEditMachineGroupForm';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';

export type CreateFolderRequest = {
  name: string;
  customerId: string | undefined;
};

interface CreateFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFolder: (name: string, customerId: string | undefined) => void;
  isCreating: boolean;
}

function CreateFolderDialog({
  isOpen,
  onClose,
  onCreateFolder,
  isCreating,
}: CreateFolderDialogProps) {
  const userProfile = useSelector(getUserProfile) as UserProfile;
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [folderName, setFolderName] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(undefined);
  const { data: customersData, isLoading: isLoadingCustomers } =
    useListCustomersQuery(false);

  const handleCreate = () => {
    if (!submitDisabled) {
      onCreateFolder(folderName.trim(), selectedCustomerId);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFolderName('');
      setSelectedCustomerId(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (userProfile?.role === PortalRole[PortalRole.Admin]) {
      setSubmitDisabled(!folderName.trim() || !selectedCustomerId);
    } else {
      setSubmitDisabled(!folderName.trim());
    }
  }, [folderName, selectedCustomerId, userProfile]);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: '1.5rem', padding: '20px' }}>
        Create New Folder
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <HighlightOffOutlined fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '20px' }}>
        <Grid container spacing={3}>
          <RoleBased
            requiredRole={PortalRole[PortalRole.Admin]}
            component={
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Customer *</InputLabel>
                  <Select
                    value={selectedCustomerId}
                    label="Customer *"
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    disabled={isLoadingCustomers}
                  >
                    {customersData?.data?.map((customer: Customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            }
          />

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Folder Name *"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              autoFocus
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={isCreating}
          variant="contained"
          onClick={handleCreate}
          disabled={submitDisabled}
        >
          Create Folder
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateFolderDialog;
